/**
 * LOTE B — Igualas (facturas recurrentes) de una oportunidad, mostradas en su
 * pestaña de Facturación junto a los hitos. Así lo recurrente SIGUE visible en
 * el plan de pagos de la oportunidad (antes solo se veía en Administración →
 * Igualas). Solo lectura: la gestión completa vive en /settings/recurring.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Repeat, ExternalLink } from 'lucide-react'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import { currentLocaleTag } from '../../lib/formatting'
import type { RecurringInvoiceDto, RecurringInvoiceListResponse } from '@toga/shared'

interface Props {
  opportunityId: string
}

/** Importe por periodo (suma de líneas: cantidad × precio unitario). */
function perPeriodCents(r: RecurringInvoiceDto): number {
  return r.lines.reduce((s, l) => s + Math.round((Number(l.quantity) || 0) * l.unitPriceCents), 0)
}

const STATUS_STYLES: Record<string, string> = {
  ACTIVE: 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  PAUSED: 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30',
  CANCELED: 'text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30',
  COMPLETED: 't-muted bg-[var(--bg-hover)] border-[var(--border)]',
}

export default function OpportunityRecurringPanel({ opportunityId }: Props) {
  const { t } = useTranslation(['billing', 'enums'])
  const [items, setItems] = useState<RecurringInvoiceDto[] | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const resp = await api.get<RecurringInvoiceListResponse>(
          `/recurring-invoices?opportunityId=${opportunityId}`
        )
        if (!cancelled) setItems(resp.items)
      } catch {
        if (!cancelled) setItems([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [opportunityId])

  if (items === null || items.length === 0) return null

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(currentLocaleTag(), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })

  return (
    <div className="surface p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2 text-sm">
          <Repeat size={15} className="text-teal-600 dark:text-teal-300" />
          {t('recurringPanel.title')}
        </h3>
        <Link
          to="/settings/recurring"
          className="text-xs t-muted hover:text-toga-700 dark:hover:text-toga-200 flex items-center gap-1"
        >
          {t('recurringPanel.manage')} <ExternalLink size={12} />
        </Link>
      </div>
      <ul className="space-y-2">
        {items.map((r) => {
          const vigencia =
            r.maxOccurrences != null
              ? t('recurringPanel.quotas', { count: r.maxOccurrences })
              : r.endsAt != null
                ? t('recurringPanel.until', { date: fmtDate(r.endsAt) })
                : t('recurringPanel.indefinite')
          return (
            <li
              key={r.id}
              className="rounded-lg border border-base/60 px-3 py-2 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="t-primary text-sm font-medium truncate">{r.name}</p>
                  <span
                    className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${
                      STATUS_STYLES[r.status] ?? STATUS_STYLES.COMPLETED
                    }`}
                  >
                    {t(`enums:RecurringInvoiceStatus.${r.status}`)}
                  </span>
                </div>
                <p className="text-xs t-muted">
                  {t(`enums:RecurringInvoicePeriodicity.${r.periodicity}`)} · {vigencia}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono t-primary text-sm">{formatCents(perPeriodCents(r))}</p>
                <p className="text-[11px] t-muted">
                  {t('recurringPanel.next', { date: fmtDate(r.nextRunDate) })}
                </p>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
